import * as React from "react";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Stack,
    Typography,
    Divider,
    Paper,
    TextField,
} from "@mui/material";
import {
    LocalizationProvider,
    StaticDatePicker,
    PickersDay,
    PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { useDispatch } from "react-redux";
import { setLoading, unsetLoading } from "../../../../store/loading/loading.slice";
import { BookingMeetingRoomApi } from "../../../../api/booking-meeting-room/booking-meeting-room.api";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

// ---------- Types & Mock Data ----------
type BookingStatus = "CONFIRM_BOOKING" | "WAITING_TO_CONSIDER";

type BookingItem = {
    id: string;
    title: string;
    date: string; // YYYY-MM-DD
    startTime: string;
    endTime: string;
    meeting_room_status: BookingStatus;
};

// const MOCK_DATA: BookingItem[] = [
//     {
//         id: "1",
//         title: "กิจกรรมติวเข้มข้อสอบเก็บคะแนน 1",
//         date: "2024-10-29",
//         startTime: "08:00",
//         endTime: "12:00",
//         meeting_room_status: "APPROVED",
//     },
//     {
//         id: "2",
//         title: "กิจกรรมปูพื้นฐานธรรมะ",
//         date: "2024-10-29",
//         startTime: "13:00",
//         endTime: "17:00",
//         meeting_room_status: "PENDING",
//     },
//     {
//         id: "3",
//         title: "สัมมนาเชิงปฏิบัติการ",
//         date: "2024-10-05",
//         startTime: "09:00",
//         endTime: "12:00",
//         meeting_room_status: "APPROVED",
//     },
//     {
//         id: "4",
//         title: "เวิร์กช็อปการเขียนแผน",
//         date: "2024-10-10",
//         startTime: "09:30",
//         endTime: "11:30",
//         meeting_room_status: "APPROVED",
//     },
// ];

const STATUS_META: Record<
    BookingStatus,
    { label: string; color: "success" | "warning"; dotColor: string }
> = {
    CONFIRM_BOOKING: { label: "อนุมัติการจอง", color: "success", dotColor: "#7BC47F" }, // i18n-ignore: display label, translated at render site with t(label)
    WAITING_TO_CONSIDER: { label: "อยู่ระหว่างพิจารณา", color: "warning", dotColor: "#FFC36A" }, // i18n-ignore: display label, translated at render site with t(label)
};

// ---------- Helpers ----------
const thMonths = [
    "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", // i18n-ignore: translated in formatMonthBE
    "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม", // i18n-ignore: translated in formatMonthBE
];

function buildDailyStatusMap(data: BookingItem[]) {
    const map = new Map<
        string,
        { CONFIRM_BOOKING: number; WAITING_TO_CONSIDER: number }
    >();
    data.forEach((e) => {
        const key = e.date;
        if (!map.has(key)) map.set(key, { CONFIRM_BOOKING: 0, WAITING_TO_CONSIDER: 0 });
        const cur = map.get(key)!;
        cur[e.meeting_room_status] += 1;
    });
    return map;
}


function formatMonthBE(d: Dayjs) {
    const monthName = i18n.t(thMonths[d.month()]);
    const beYear = d.year() + (i18n.language === "en" ? 0 : 543);
    return `${monthName} ${beYear}`;
}


function CalendarDay(
    props: PickersDayProps<Dayjs> & {
        bookings?: { CONFIRM_BOOKING: number; WAITING_TO_CONSIDER: number };
    }
) {
    const { bookings, day, ...rest } = props;
    return (
        <Box sx={{ position: "relative", width: "100%" }}>
            <PickersDay day={day} {...rest} />
            {bookings && (bookings.CONFIRM_BOOKING > 0 || bookings.WAITING_TO_CONSIDER > 0) && (
                <Box sx={{ position: "absolute", left: "50%", bottom: 4, transform: "translateX(-50%)" }}>
                    <Stack direction="row" spacing={0.5}>
                        {bookings.WAITING_TO_CONSIDER > 0 && (
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: STATUS_META.WAITING_TO_CONSIDER.dotColor,
                                }}
                            />
                        )}
                        {bookings.CONFIRM_BOOKING > 0 && (
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: STATUS_META.CONFIRM_BOOKING.dotColor,
                                }}
                            />
                        )}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}

// ---------- Main Component ----------
type Props = {
    id_room_meeting?: number;
}

export default function BookingCalendarLegacy({ id_room_meeting }: Props) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [value, setValue] = React.useState<Dayjs>(dayjs());
    const [monthRef, setMonthRef] = React.useState<Dayjs>(dayjs());

    const [data, set_data] = React.useState<BookingItem[]>([])

    const selectedDateStr = value.format("YYYY-MM-DD");
    const itemsOfSelectedDate = data.filter((e) => e.date === selectedDateStr);

    const handleMonthChange = (newMonth: Dayjs) => {
        setMonthRef(newMonth.startOf("month"));
    };


    const handleGetData = async () => {
        dispatch(setLoading());
        await BookingMeetingRoomApi.bookingCalendar(id_room_meeting).then((res) => {
            if (res.status < 300) {
                const response = res.data?.data || [];
                set_data(response)
                // console.log(response, 'response')
            }
        }).catch((err) => {
            console.log(err)
        }).finally(() => {
            dispatch(unsetLoading());
        })
    }
    const dailyStatusMap = buildDailyStatusMap(data);


    React.useEffect(() => {
        handleGetData();
    }, [])

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language === "en" ? "en" : "th"}>
            <Box sx={{ display: "grid", gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatMonthBE(monthRef)}
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        width: "100%",
                        "& .MuiDateCalendar-root": {
                            width: "100% !important",        
                        },
                        "& .MuiDayCalendar-weekContainer": {
                            justifyContent: "space-between", 
                        },
                        "& .MuiPickersDay-root": {
                            flex: "1 0 14.2857%",
                            maxWidth: "none",
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: "100% !important",        
                            "& .MuiPickerStaticWrapper-root, & .MuiDateCalendar-root, & .MuiCalendarPicker-root": {
                                width: "100% !important",
                            },
                            "& .MuiDayCalendar-weekContainer": {
                                justifyContent: "space-between",
                            },
                            "& .MuiPickersDay-root": {
                                flex: "1 0 14.2857%",
                                maxWidth: "none",
                            },
                        }}
                    >
                        <StaticDatePicker
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    sx={{ display: "none" }}
                                />
                            )}
                            className="w-full"
                            displayStaticWrapperAs="desktop"
                            value={value}
                            onChange={(newVal) => newVal && setValue(newVal)}
                            onMonthChange={handleMonthChange}
                            renderDay={(day, _value, DayComponentProps) => {
                                const key = day.format("YYYY-MM-DD");
                                const bookings = dailyStatusMap.get(key);
                                return (
                                    <CalendarDay
                                        {...DayComponentProps}
                                        day={day}
                                        bookings={bookings}
                                    />
                                );
                            }}
                        />
                    </Box>

                    <Stack direction="row" spacing={2} mt={1}>
                        <Stack direction="row" spacing={1}>
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    bgcolor: STATUS_META.WAITING_TO_CONSIDER.dotColor,
                                }}
                            />
                            <Typography variant="body2">
                                {t(STATUS_META.WAITING_TO_CONSIDER.label)}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    bgcolor: STATUS_META.CONFIRM_BOOKING.dotColor,
                                }}
                            />
                            <Typography variant="body2">
                                {t(STATUS_META.CONFIRM_BOOKING.label)}
                            </Typography>
                        </Stack>
                    </Stack>
                </Paper>



                <Stack spacing={2}>
                    {itemsOfSelectedDate.map((ev) => (
                        <Card key={ev.id} variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Typography sx={{ fontWeight: 600 }}>{ev.title}</Typography>
                                <Stack direction="row" spacing={2} mt={1}>
                                    <Typography variant="body2">
                                        {t("วันที่ :")} {dayjs(ev.date).format("DD/MM/YY")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t("เวลา :")} {ev.startTime} - {ev.endTime}
                                    </Typography>
                                </Stack>
                                <Chip
                                    size="small"
                                    label={t(STATUS_META[ev.meeting_room_status].label)}
                                    color={STATUS_META[ev.meeting_room_status].color}
                                    sx={{ mt: 1 }}
                                />
                            </CardContent>
                        </Card>
                    ))}

                    {itemsOfSelectedDate.length === 0 && (
                        <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                            <Typography>{t("ไม่มีรายการในวันที่เลือก")}</Typography>
                        </Box>
                    )}
                </Stack>
                <Divider />
            </Box>
        </LocalizationProvider>
    );
}
